import { useId, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Modale, Scelta } from '../../componenti/base.tsx'
import * as elenco from '../../servizi/posta-elenco.ts'
import '../../stile-posta-elenco.css'

/**
 * «Elenco e lettura» (541; come le impostazioni di Roundcube): ordine, vista per conversazioni o per messaggi,
 * ordine dei messaggi nella conversazione aperta (4/11/2026), righe per pagina, quando un messaggio aperto
 * diventa letto, dove si scrive la risposta. Si salvano sul server per la persona: valgono su ogni dispositivo
 * e su tutte le caselle che legge.
 */
export function PreferenzePosta({ attuali, chiudi, salvate, tastiera }: {
  attuali: elenco.PreferenzePosta
  chiudi: () => void
  salvate: (p: elenco.PreferenzePosta) => void
  /** Apre l'elenco delle scorciatoie da tastiera. */
  tastiera: () => void
}) {
  const [p, setP] = useState(attuali)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const id = useId()
  const cambia = (parte: Partial<elenco.PreferenzePosta>) => setP((v) => ({ ...v, ...parte }))

  async function salva() {
    setInCorso(true); setErrore(null)
    try { salvate(await elenco.salvaPreferenze(p)) } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: riprova.')
    } finally { setInCorso(false) }
  }

  const scelta = (nome: string, valore: string, attuale: string, etichetta: string, cambiaA: () => void) => (
    <label className="posta-preferenza-scelta">
      <input type="radio" name={`${id}-${nome}`} value={valore} checked={attuale === valore} onChange={cambiaA} /> {etichetta}
    </label>
  )

  return (
    <Modale titolo="Elenco e lettura" chiudi={chiudi}
            azioni={(
              <>
                <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>Salva</Bottone>
              </>
            )}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="posta-preferenze">
        <Scelta etichetta="Ordina per" valore={`${p.ordine}:${p.verso}`}
                cambia={(v) => { const [ordine, verso] = v.split(':') as [elenco.OrdineElenco, elenco.Verso]; cambia({ ordine, verso }) }}
                opzioni={elenco.ORDINI.map((o) => ({ valore: `${o.ordine}:${o.verso}`, etichetta: o.nome }))}
                aiuto="Per mittente e oggetto si ordinano le 2000 conversazioni più recenti; con la ricerca vale la data." />
        <fieldset className="posta-preferenza">
          <legend>Vista</legend>
          {scelta('vista', 'conversazioni', p.vista, 'Per conversazioni: i messaggi di una conversazione insieme',
            () => cambia({ vista: 'conversazioni' }))}
          {scelta('vista', 'messaggi', p.vista, 'Per messaggi: una riga per ogni messaggio',
            () => cambia({ vista: 'messaggi' }))}
        </fieldset>
        <fieldset className="posta-preferenza">
          <legend>Nella conversazione aperta</legend>
          {scelta('conversazione', 'recenti_sopra', p.conversazione, 'il messaggio più recente in alto',
            () => cambia({ conversazione: 'recenti_sopra' }))}
          {scelta('conversazione', 'recenti_sotto', p.conversazione, 'il messaggio più recente in fondo',
            () => cambia({ conversazione: 'recenti_sotto' }))}
        </fieldset>
        <Scelta etichetta="Righe per pagina" valore={String(p.righe)} cambia={(v) => cambia({ righe: Number(v) as elenco.PreferenzePosta['righe'] })}
                opzioni={elenco.RIGHE.map((n) => ({ valore: String(n), etichetta: String(n) }))} />
        <fieldset className="posta-preferenza">
          <legend>Un messaggio aperto diventa letto</legend>
          {scelta('lettura', 'apertura', p.lettura, 'appena lo apri', () => cambia({ lettura: 'apertura' }))}
          <span className="posta-preferenza-dopo">
            {scelta('lettura', 'dopo', p.lettura, 'dopo', () => cambia({ lettura: 'dopo' }))}
            <input type="number" min={1} max={120} value={p.secondi} aria-label="Secondi prima che diventi letto"
                   onChange={(e) => cambia({ lettura: 'dopo', secondi: Math.min(120, Math.max(1, Number(e.target.value) || 1)) })} />
            secondi
          </span>
          {scelta('lettura', 'mai', p.lettura, 'mai da solo: con «Segna come letta»', () => cambia({ lettura: 'mai' }))}
        </fieldset>
        <fieldset className="posta-preferenza">
          <legend>Rispondendo, scrivo</legend>
          {scelta('risposta', 'sopra', p.risposta, 'sopra il messaggio citato', () => cambia({ risposta: 'sopra' }))}
          {scelta('risposta', 'sotto', p.risposta, 'sotto il messaggio citato', () => cambia({ risposta: 'sotto' }))}
        </fieldset>
        <p className="posta-nota">Le preferenze valgono per te, su ogni computer e telefono e su tutte le caselle che leggi.</p>
        <p className="posta-nota">
          <button type="button" className="posta-tutte-lette" onClick={tastiera}>Scorciatoie da tastiera</button>
          {' '}(anche con il tasto «?» sopra l’elenco).
        </p>
      </div>
    </Modale>
  )
}
